<template>
  <v-container fluid>
    <v-row class="d-flex flex-row">
      <v-col cols="12">
        <FormPageTouristAgency
          :data="touristAgency"
          :cities="cities"
          :routes="routes"
          :audioguides="audioguides"
          :loading="isFormLoading"
          :disabled="isMutationLoading"
          :isNew="!id"
          :language="language"
          @submit="formTouristAgencySubmitHandler"
          @delete-tourist-agency="deleteTouristAgency"
          @language-update="language = $event"
          @change:descriptionForMobile="changeDescForMobHandler($event)"
        />
      </v-col>
    </v-row>
    <ConfirmationDialog ref="confirmTouristAgencyRemoving" confirmText="Удалить" />
  </v-container>
</template>

<script>
import { contentLocales, contentLocalesValues } from '@/configs/locales'
import { getListWithNewMainLanguage, setTranslation } from '@/utils/locales'

import FormPageTouristAgency from './form'
import ConfirmationDialog from '@/components/confirmation-dialog'

import citiesQuery from '@/services/gql/types/City/queries/citiesGrid.gql'
import audioguidesQuery from '@/services/gql/types/Audioguides/queries/audioguidesGrid.gql'
import routesQuery from '@/services/gql/types/Routes/queries/routesGrid.gql'
import touristAgencyQuery from '@/services/gql/types/TouristAgencies/queries/touristAgency.gql'
import createTourisAgency from '@/services/gql/types/TouristAgencies/mutations/createTouristAgency.gql'
import updateTouristAgency from '@/services/gql/types/TouristAgencies/mutations/updateTouristAgency.gql'
import deleteTourisAgency from '@/services/gql/types/TouristAgencies/mutations/deleteTouristAgency.gql'
import sharedAccessQuery from '@/services/gql/types/SharedAccess/queries/sharedAccess.gql'
import ticketReservationsOfRelations
from '@/services/gql/types/TicketReservations/queries/ticketReservationsOfRelations.gql'

export default {
  name: 'PageTouristAgency',
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    FormPageTouristAgency,
    ConfirmationDialog
  },
  metaInfo () {
    return {
      title: this.id ? 'Редактировать туристическое агенство' : 'Создать туристическое агенство'
    }
  },
  data () {
    return {
      language: contentLocales[0].value,
      cities: null,
      routes: null,
      audioguides: null,
      touristAgency: null,
      sharedAccess: null,
      ticketReservations: null
    }
  },
  computed: {
    isMutationLoading () {
      return this.$async.createTourisAgency.$pending || this.$async.updateTouristAgency.$pending
    },
    isFormLoading () {
      return (
        this.$apollo.queries.touristAgency.loading ||
        this.$apollo.queries.audioguides.loading ||
        this.$apollo.queries.cities.loading ||
        this.$apollo.queries.routes.loading

      )
    }
  },
  methods: {
    changeDescForMobHandler ({ fields, $event }) {
      fields.descriptionForMobile = $event
    },
    async formTouristAgencySubmitHandler (touristAgencyPayload) {
      if (!this.id) await this.createTouristAgency(touristAgencyPayload)
      else await this.updateTouristAgency(touristAgencyPayload)
    },
    async createTouristAgency ({ data }) {
      delete data.translations
      try {
        await this.$async.createTourisAgency.$perform({
          data
        })
        this.$successMsg('Тур. оператор/агенство создано')
        await this.$router.push({ name: 'page-tourist-agencies' })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании тур. оператора/агенства')
      }
    },
    async deleteTouristAgency () {
      try {
        await this.$refs.confirmTouristAgencyRemoving.open()
        await this.$async.deleteTourisAgency.$perform({ id: Number(this.id) })
        this.$successMsg('Тур. оператор/агенство удалено')
      } catch (e) {
        console.log(e)
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении тур. оператора/агенства с ID ${this.id}`)
      } finally {
        await this.$router.push({ name: 'page-tourist-agencies' })
      }
    },
    async updateTouristAgency ({ data }) {
      try {
        setTranslation(data, this.language)
        await this.$async.updateTouristAgency.$perform(
          {
            id: Number(this.id),
            data
          },
          { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) }
        )
        this.$successMsg('Тур. оператор/агенство изменено')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при редактировании Тур. оператора/агенства')
      }
    }
  },
  apollo: {
    touristAgency: {
      query: touristAgencyQuery,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            id: Number(this.id)
          }
        }
      },
      context () {
        return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } }
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-tourist-agencies' })
        } else {
          this.touristAgency = res?.data?.touristAgency || null
        }
      }
    },
    cities: {
      query: citiesQuery,
      fetchPolicy: 'network-only',
      pollInterval: 30000,
      variables: {
        where: {}
      },
      result (res) {
        this.cities = res?.data?.cities?.list || null
      }
    },
    routes: {
      query: routesQuery,
      fetchPolicy: 'network-only',
      pollInterval: 30000,
      variables: {
        where: {}
      },
      result (res) {
        this.routes = res?.data?.routes?.list || null
      }
    },
    audioguides: {
      query: audioguidesQuery,
      fetchPolicy: 'network-only',
      pollInterval: 30000,
      variables: {
        where: {}
      },
      result (res) {
        this.audioguides = res?.data?.audioguides?.list || null
      }
    },
    sharedAccess: {
      query: sharedAccessQuery,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            profile: {
              id: Number(this.id)
            }
          }
        }
      },
      context () { return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } } },
      result (res) {
        this.sharedAccess = res?.data?.sharedAccess?.list
      }
    },
    ticketReservations: {
      query: ticketReservationsOfRelations,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            profileType: 'TOURIST_AGENCY',
            profileId: Number(this.id)
          }
        }
      },
      context () { return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } } },
      result (res) {
        const { list } = res?.data?.ticketReservations
        const tickets = list
          ?.map(ticket => ({ count: 1, name: ticket.displayName }))
          ?.reduce((acc, ticket) => {
            acc[ticket.name] = (acc[ticket.name] || 0) + ticket.count
            return acc
          }, {}) || null
        this.ticketReservations = Object.keys(tickets)?.map(ticket => ({
          title: ticket + ': ' + tickets[ticket]
        }))
      }
    }
  },
  async: {
    createTourisAgency (variables) {
      return this.$apollo.mutate({
        mutation: createTourisAgency,
        variables
      })
    },
    deleteTourisAgency (variables) {
      return this.$apollo.mutate({
        mutation: deleteTourisAgency,
        variables
      })
    },
    updateTouristAgency (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateTouristAgency,
        variables,
        context: {
          headers
        }
      })
    }
  }
}
</script>
